// src/pages/RequestDemo.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, Variants } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Play, Zap, CheckCircle, Home } from "lucide-react";

/* ---------------- ANIMATION ---------------- */
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

/* ---------------- COMPONENT ---------------- */
const RequestDemo: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const res = await fetch("/api/request-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to send request");

      setSubmitted(true);
      setFormData({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30">
      {/* NAVBAR */}
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-1 flex h-16 items-center justify-between">
          <img
            src="/assets/logo/rnb-logo.png"
            alt="RetailNextBox"
            className="h-60 w-80"
          />
          <div className="flex gap-3">
            <Link to="/pricing">
              <Button className="bg-gradient-primary">View Pricing</Button>
            </Link>
            {/* <Link to="/demo">
              <Button className="bg-gradient-primary">Request Demo</Button>
            </Link> */}
          </div>
        </div>
      </nav>

      {/* HERO / FORM */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <Badge className="bg-primary/10 text-primary inline-flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Personalized Demo
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-blue-600 mt-4">
            Get Your Personalized Demo
          </h1>
          <p className="text-muted-foreground mt-2">
            Fill out the form below and our team will contact you with a tailored
            demo for your retail business.
          </p>
        </motion.div>

        <Card className="shadow-xl">
          <CardContent className="p-8 space-y-6">
            {submitted ? (
              <div className="text-green-600 font-semibold text-center">
                ✅ Your demo request has been sent! We will contact you shortly.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <Textarea
                  name="message"
                  placeholder="Message (optional)"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                />
                {error && <p className="text-red-600">{error}</p>}
                <Button type="submit" className="bg-blue-600 text-white w-full">
                  Request Demo
                </Button>
              </form>
            )}

            {/* Back to Home Button */}
            <div className="mt-6 text-center">
              <Link to="/">
                <Button variant="outline" className="inline-flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FOOTER */}
      <footer className="border-t py-8 text-center text-sm text-muted-foreground">
        © 2025 RetailNextBox. Built for the future of retail.
      </footer>
    </div>
  );
};

export default RequestDemo;
